import axios from 'axios';
import { RequestConfig } from '@/../types/interface/config';
import { getTokenMerge, logout } from '@/utils/ways';
import { getCookie, getStorage } from '@lhb/cache';
import { deepCopy } from '@lhb/func';
import CryptoJS from 'crypto-js';

const key = 'QQUBsuqU7quVTzbkvmq3sJwRChiRIUg5';
const iv = '9826336351614201';

/**
 * 接口请求解密
 * @param encrypted 密文
 * @param key 公钥
 * @param iv 偏移量
 * @returns
 */
function aesDecrypt(encrypted: string, key: string, iv: string) {
  // 将密文和密钥转换为CryptoJS支持的格式
  const encryptedData = CryptoJS.enc.Hex.parse(encrypted);
  const decryptedKey = CryptoJS.enc.Utf8.parse(key);
  const decryptedIV = CryptoJS.enc.Utf8.parse(iv);

  // 执行解密操作
  const decrypted = CryptoJS.AES.decrypt(
    { ciphertext: encryptedData },
    decryptedKey,
    { iv: decryptedIV },
  );

  // 获取解密结果
  const decryptedText = decrypted.toString(CryptoJS.enc.Utf8);

  return JSON.parse(decryptedText);
}

// MockUrl
const mockUrl = 'https://yapi.lanhanba.com/mock/';
const baseUrl = '/api';
/**
 * 请求基础配置
 */
const baseConfig: any = {
  needCancel: true, // 是否需要在下次请求发生时，对之前的请求进行 abortControl
  isMock: false, // 是否mock方式
  mockId: 319, // 其他后台mock的id
  needHint: false, // 是否需要主动报错
  proxyApi: '', // 需要代理的其他 api目标， 会替换掉当前目标的 /lone
  version: 'v1', // 接口版本
  errorConfig: {
    needHint: true, // 考虑到微前端的问题，暂时改成 true,以后空了回头来解决
    duration: 3, // 报错后多久小时，秒
    showClose: false, // 是否显示关闭按钮，开启后报错弹窗将不会主动消失
  },
};

/**
 * 异常处理
 */
class ApiError extends Error {
  code: string;
  constructor(err: { message?: string; msg?: string; code: string }) {
    const msg: string = err.message || err.msg || '';
    super(msg);
    Object.assign(this, err);
    this.code = err.code;
  }
}

/**
 * 获取header
 * @param _version 版本号
 * @param isPost 是否是 Post 请求
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export function getHeader(_version = 1, isPost = false): any {
  // 需要判断是微前端环境以及跳转取kunlun的token
  const apiKey = getTokenMerge();
  const getTokenApi: any = getStorage('getTokenApi');

  const placeIds = typeof getCookie('placeIds') === 'string' ? getCookie('placeIds') : 0;
  const config = {
    // Accept: `application/vnd.linhuiba.v${version}+json`,
    // Authorization: apiKey ? `Bearer ${apiKey}` : 'Bearer',
    clientKey: 1000010,
    placeIds: placeIds,
  };

  if (apiKey) {
    config['token'] = apiKey;
    config['originToken'] = JSON.stringify(apiKey);

  }

  if (getTokenApi) {
    config['tokenApi'] = getTokenApi;
  }

  return isPost ? { ...config, ...{ 'Content-Type': 'application/json' } } : config;
}

/**
 * 合并请求
 * @param extraConfig
 */
export function mergeConfig(extraConfig: any): RequestConfig {
  const _baseConfig = deepCopy(baseConfig);
  // 为了方便 第三个参数为boolean 值时可控制needHint值
  if (typeof extraConfig === 'boolean') {
    _baseConfig.errorConfig.needHint = extraConfig;
    return _baseConfig;
  } else if (extraConfig.needHint) {
    _baseConfig.errorConfig.needHint = extraConfig.needHint;
  }
  return { ..._baseConfig, ...extraConfig };
}

/**
 * 返回配置后的请求URl
 */
export function getRequestUrl(serviceName: string, config: RequestConfig): string {
  return (
    (config.isMock ? `${mockUrl}${config.mockId || baseConfig.mockId}` : `${config.proxyApi || '/pms'}`) + (config.proxyApi ? baseUrl : `/${config.version}`) + serviceName
  );
}

/**
 * 错误的请求处理
 * @param err
 * @param config
 */
export function fail(err: any, config: RequestConfig, serviceName: string): null {
  if (axios.isCancel(err)) {
    throw new ApiError({
      code: 'cancel',
      msg: `您取消了请求 ${serviceName}`,
    });
  }

  const needHint = config.errorConfig && config.errorConfig.needHint;
  const duration = config.errorConfig && config.errorConfig.duration;
  const showClose = (config.errorConfig && config.errorConfig.showClose) || false;
  const errorOptions: any = {
    type: 'error',
    duration,
    closable: showClose,
  };
  if (err.response) {
    const {
      status,
      data: { errMsg },
    } = err.response;
    switch (status) {
      case 401:
        errorOptions.content = '未登录';
        break;
      case 403:
        errorOptions.content = '没有权限';
        break;
      case 500:
        errorOptions.content = '系统错误';
        break;
      default:
        if (needHint) {
          errorOptions.content = errMsg;
        }
        break;
    }
    if (errorOptions.content) {
      if (status === 401) {
        logout();
      } else {
        window.microMessage.open(errorOptions);
      }
    }
  }
  throw new ApiError(err);
}

/**
 * 成功请求处理
 * remark: 暂时未做处理，预留
 * @param res
 */
export function success(res: any, config: any) {
  let data = res.data;

  if (typeof data === 'string') {
    data = aesDecrypt(data, key, iv);
    console.info(`request = `, data);
  }

  if (config.isMock) {
    return data;
  }
  if (data) {
    return data;
  } else {
    // AntdMessage.open({
    //   type: 'error',
    //   content: data.msg,
    // });
  }
  throw new ApiError(data);
}

/**
 * 获取 axios 请求配置
 * @param requestConfig
 * @param axiosConfig
 */
export function getAxiosConfig(requestConfig: RequestConfig, axiosConfig: any = {}) {
  const { needCancel = true, timeout } = requestConfig;
  // 需要cancel的时候，就加入cancelToken
  if (needCancel) {
    // axios会根据 cancelToken是否存在，进行打断。
    axiosConfig.cancelToken = {};
  }
  if (timeout) {
    axiosConfig.timeout = timeout;
  }
  return axiosConfig;
}
